/**
 * Court Piece (Rang / Rung) — server-authoritative rules engine (South Asian partnership trick-taking, 4 players).
 *
 * Rules implemented (toggles live in {@link CourtPieceConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). One 52-card deck, ace high.
 *  - The "caller" (the player next to the dealer) first receives only 5 cards, looks at them and names the trump
 *    suit ("rang"). The rest of the deck is then dealt out: the caller gets 8 more, everyone else 13.
 *  - The caller leads the first trick; the trick winner leads next. Players must follow the led suit when able;
 *    a void player may play anything. Trick winner: highest trump, else highest card of the led suit.
 *  - A team that wins 7 or more of the 13 tricks wins the hand (a "court"). Taking all 13 tricks (a "Court" sweep)
 *    is worth `sweepPoints` hand-points, any other win is worth 1.
 *  - After a hand the caller keeps the role when their team won it; otherwise it passes to the next player.
 *  - The match ends when a team reaches `targetScore` hand-points, or after `maxRounds` hands (the leader wins).
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface CourtPieceConfig {
  /** Match ends when a team reaches this many hand-points. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxRounds: number;
  /** Hand-points for taking all 13 tricks. */
  sweepPoints: number;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type CourtPiecePhase = 'TRUMP' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface CourtPieceTrickPlay {
  seat: number;
  card: CardId;
}

export interface CourtPieceCompletedTrick {
  leader: number;
  plays: CourtPieceTrickPlay[];
  winner: number;
}

/** Per-hand result for one team (public once the hand ends). */
export interface CourtPieceTeamRound {
  tricks: number;
  /** Hand-points gained by the team this hand. */
  delta: number;
  /** True for the team that won the hand. */
  won: boolean;
  /** True when the team took all 13 tricks. */
  sweep: boolean;
}

export interface CourtPieceState {
  config: CourtPieceConfig;
  players: string[];
  round: number;
  dealer: number;
  /** The seat that names trump and leads the first trick. Always `dealer + 1`. */
  caller: number;
  phase: CourtPiecePhase;
  hands: CardId[][];
  /** Cards still to be dealt once trump has been named (hidden from everyone). */
  stock: CardId[];
  trump: Suit | null;
  tricksWon: number[];
  turn: number;
  trick: CourtPieceTrickPlay[];
  tricks: CourtPieceCompletedTrick[];
  teamScores: number[];
  roundResults: CourtPieceTeamRound[][];
}

export type CourtPieceAction = { type: 'trump'; suit: Suit } | { type: 'play'; card: CardId };

export type CourtPieceLegal = { kind: 'trump'; suits: Suit[] } | { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface CourtPieceView {
  gameType: 'COURT_PIECE';
  config: CourtPieceConfig;
  seat: number | null;
  phase: CourtPiecePhase;
  round: number;
  dealer: number;
  caller: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  trump: Suit | null;
  tricksWon: number[];
  trick: CourtPieceTrickPlay[];
  lastTrick: CourtPieceCompletedTrick | null;
  tricks: CourtPieceCompletedTrick[];
  teamScores: number[];
  roundResults: CourtPieceTeamRound[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: CourtPieceConfig = {
  targetScore: 5,
  maxRounds: 15,
  sweepPoints: 2,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const FIRST_DEAL = 5;
const WIN_TRICKS = 7;

export const courtPieceTeamOf = (seat: number): number => seat % 2;
export const courtPiecePartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function courtPieceTrickWinner(plays: readonly CourtPieceTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === trump && b.suit !== trump) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === trump || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function courtPieceLegalCards(hand: readonly CardId[], trick: readonly CourtPieceTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** Suit the caller holds most strongly among their first five cards (length first, then honours). */
export function courtPieceBestSuit(hand: readonly CardId[]): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const ranks = hand.map(parseCard).filter((c) => c.suit === suit).map((c) => c.rank);
    const score = ranks.length * 10 + ranks.reduce((n, r) => n + (r >= 11 ? r - 9 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

/** Hand-point change for each team given the tricks they took. */
export function courtPieceScoreRound(tricks: readonly [number, number], cfg: CourtPieceConfig): CourtPieceTeamRound[] {
  return [0, 1].map((team) => {
    const mine = tricks[team]!;
    const won = mine >= WIN_TRICKS;
    const sweep = mine === 13;
    return { tricks: mine, won, sweep, delta: won ? (sweep ? cfg.sweepPoints : 1) : 0 };
  });
}

function deal(state: CourtPieceState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  state.caller = (state.dealer + 1) % SEATS;
  const hands: CardId[][] = [[], [], [], []];
  hands[state.caller] = sortHand(deck.slice(0, FIRST_DEAL));
  state.hands = hands;
  state.stock = deck.slice(FIRST_DEAL);
  state.trump = null;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'TRUMP';
  state.turn = state.caller;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, caller: state.caller, counts: state.hands.map((h) => h.length) });
}

/** Deals the rest of the deck once trump is named: the caller tops up to 13, everyone else gets 13. */
function dealRest(state: CourtPieceState): void {
  const stock = state.stock;
  let i = 0;
  for (let n = 1; n <= SEATS; n++) {
    const seat = (state.caller + n) % SEATS;
    const need = 13 - state.hands[seat]!.length;
    state.hands[seat] = sortHand([...state.hands[seat]!, ...stock.slice(i, i + need)]);
    i += need;
  }
  state.stock = [];
}

export const courtPieceEngine: GameEngine<CourtPieceConfig, CourtPieceState, CourtPieceAction, CourtPieceView, CourtPieceLegal> = {
  gameType: 'COURT_PIECE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 21);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('sweepPoints', c.sweepPoints, 1, 5);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Court Piece needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: CourtPieceState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      caller: 0,
      phase: 'TRUMP',
      hands: [],
      stock: [],
      trump: null,
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'trump' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'trump', suit: raw.suit as Suit };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Court Piece action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'TRUMP' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'trump') {
      assertEngine(state.phase === 'TRUMP', 'INVALID_PHASE', 'Trump is not being chosen');
      state.trump = action.suit;
      dealRest(state);
      state.phase = 'PLAYING';
      state.turn = seat;
      events.push({ type: 'trumpChosen', seat, suit: action.suit });
      events.push({ type: 'dealtRest', counts: state.hands.map((h) => h.length) });
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(courtPieceLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = courtPieceTrickWinner(state.trick, state.trump!);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'TRUMP' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'TRUMP') return { kind: 'trump', suits: [...SUITS] };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: courtPieceLegalCards(state.hands[seat]!, state.trick) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'TRUMP') return { type: 'trump', suit: courtPieceBestSuit(state.hands[seat]!) };
    const legal = courtPieceLegalCards(state.hands[seat]!, state.trick);
    const trump = state.trump!;
    // Lowest legal card, keeping trumps for last: rarely wastes a winner, never illegal.
    const lowest = [...legal].sort((a, b) => {
      const ca = parseCard(a);
      const cb = parseCard(b);
      return (ca.suit === trump ? 100 : 0) + ca.rank - ((cb.suit === trump ? 100 : 0) + cb.rank);
    })[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'COURT_PIECE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      caller: state.caller,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trump: state.trump,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[courtPieceTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const tricks: [number, number] = [0, 1].map((team) => state.tricksWon[team]! + state.tricksWon[team + 2]!) as [number, number];
      const results = courtPieceScoreRound(tricks, cfg);
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const [a, b] = state.teamScores as [number, number];
      if (a >= cfg.targetScore || b >= cfg.targetScore || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      const last = state.roundResults.at(-1)!;
      // The caller keeps the role while their team keeps winning; otherwise it moves to the next player.
      const callerKeeps = last[courtPieceTeamOf(state.caller)]!.won;
      const nextCaller = callerKeeps ? state.caller : (state.caller + 1) % SEATS;
      state.round++;
      state.dealer = (nextCaller + SEATS - 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: CourtPieceState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = courtPieceTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
